import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Link,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SendIcon from "@mui/icons-material/Send";
import { useNavigate } from "react-router-dom";

import api from "../services/api/axios";

const successMessage =
  "If an account exists with that email, a password reset link has been sent.";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);

  const handleSubmit = async () => {
    if (!email.trim()) {
      setError("Please enter your email address.");
      return;
    }

    setLoading(true);
    setError("");
    setSent(false);

    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        "Unable to submit password reset request.";
      setError(typeof msg === "string" ? msg : "Unable to submit password reset request.");
    } finally {
      setLoading(false);
    }
  };

  const onKeyDown = (event) => {
    if (event.key === "Enter") {
      handleSubmit();
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100vw",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
        background: "linear-gradient(135deg, rgb(16,105,13), rgb(25,120,199))",
      }}
    >
      <Paper
        elevation={10}
        sx={{
          width: "100%",
          maxWidth: 680,
          p: { xs: 4, sm: 6, md: 7 },
          borderRadius: 4,
          backgroundColor: "#ffffffee",
          backdropFilter: "blur(8px)",
          border: "3px solid #00336C",
        }}
      >
        <Typography
          component="div"
          aria-label="ProspectEngage CRM"
          sx={{
            textAlign: "center",
            fontSize: { xs: 34, sm: 44 },
            fontWeight: 900,
            lineHeight: 1,
            mb: 4,
          }}
        >
          <Box component="span" sx={{ color: "#00336C" }}>
            Prospect
          </Box>
          <Box component="span" sx={{ color: "#648315" }}>
            Engage
          </Box>
        </Typography>

        <Typography variant="h4" sx={{ color: "#00336C", fontWeight: 800, mb: 1.5 }}>
          Forgot Password
        </Typography>
        <Typography sx={{ color: "#425466", mb: 4, lineHeight: 1.6, fontSize: 18 }}>
          Enter your account email and we will send a secure reset link.
        </Typography>

        <TextField
          label="Email"
          fullWidth
          margin="normal"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          onKeyDown={onKeyDown}
          autoComplete="username"
          type="email"
          inputProps={{ inputMode: "email" }}
        />

        {sent && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {successMessage}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        <Button
          variant="contained"
          startIcon={loading ? null : <SendIcon />}
          fullWidth
          onClick={handleSubmit}
          disabled={loading}
          sx={{
            mt: 4,
            py: 1.65,
            borderRadius: 3,
            backgroundColor: "#00336C",
            fontWeight: "bold",
            textTransform: "none",
            "&:hover": { backgroundColor: "#115293" },
          }}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : "Send Reset Link"}
        </Button>

        <Box sx={{ mt: 2, textAlign: "center" }}>
          <Link
            component="button"
            type="button"
            underline="hover"
            onClick={() => navigate("/login")}
            sx={{ color: "#00336C", fontWeight: 700 }}
          >
            <ArrowBackIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: "text-bottom" }} />
            Back to Login
          </Link>
        </Box>
      </Paper>
    </Box>
  );
}
